import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import ReactSwitch from 'react-switch';
import { closeChildMissingTermsPopup } from '~/actions';
import { OutlineWarning01Icon } from '@tempmee/react-icons';
import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { ConfirmPopup, Row } from '~/commonComponents';
import { useAcceptGoTuTerms } from '~/domain/AcceptGoTuTerms';
import { useAcceptPSATerms } from '~/domain/AcceptPSATerms';
import { officeMissingInfoKeys } from '~/domain/OfficeMissingInfo';
import { showErrorMessage } from '~/domain/utils';
import { useAppSelector } from '~/hooks/useAppSelector';
import {
  getPopupType,
  isTermsAlreadyAcceptedError,
  POPUP_CONFIGS,
  renderSwitchText,
} from './utils';

// Fixed copy for any refusal but "terms already accepted" (a 400 taken as agreed), such as a 403: the
// server's own message is not written for an office stuck on a popup it cannot close.
const ACCEPT_FAILED_MESSAGE = "We couldn't record your agreement. Please try again.";

const ignoreAlreadyAccepted = (error: unknown) => {
  if (!isTermsAlreadyAcceptedError(error)) throw error;
};

export const ShowChildMissingTermsPopup = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { isTCMissing, isPSAMissing, officeId } = useAppSelector((state) => state.multiday);
  const [isAccepted, setIsAccepted] = useState(false);
  const acceptPSATermsMutation = useAcceptPSATerms();
  const acceptGoTuTermsMutation = useAcceptGoTuTerms();

  const popupType = useMemo(
    () => getPopupType(isTCMissing, isPSAMissing),
    [isTCMissing, isPSAMissing],
  );
  const popupConfig = popupType ? POPUP_CONFIGS[popupType] : null;

  const isLoading = acceptPSATermsMutation.isPending || acceptGoTuTermsMutation.isPending;

  const handleToggleSwitch = useCallback(() => {
    setIsAccepted((prev) => !prev);
  }, []);

  const handleOnPress = useCallback(async () => {
    if (!popupType || !officeId) return;

    // The popup cannot be closed without agreeing, so a failed accept says why instead of
    // leaving the office on a button that does nothing.
    try {
      const acceptPsa = () =>
        acceptPSATermsMutation.mutateAsync({ officeId }).catch(ignoreAlreadyAccepted);
      const acceptGoTuTerms = () =>
        acceptGoTuTermsMutation.mutateAsync({ officeId }).catch(ignoreAlreadyAccepted);

      switch (popupType) {
        case 'psa':
          await acceptPsa();
          break;
        case 'gotuTerms':
          await acceptGoTuTerms();
          break;
        case 'both':
          await Promise.all([acceptGoTuTerms(), acceptPsa()]);
          break;
        default:
          break;
      }
    } catch {
      showErrorMessage({ error: ACCEPT_FAILED_MESSAGE });
      return;
    }

    // The shift request's PSA gate reads the picked office's terms from this record; re-read it so
    // the office reads as accepted.
    queryClient.invalidateQueries({ queryKey: officeMissingInfoKeys.office(officeId) });
    // Mounted for the whole wizard, so the next office it opens for must agree on its own.
    setIsAccepted(false);
    dispatch(closeChildMissingTermsPopup());
  }, [
    popupType,
    officeId,
    acceptGoTuTermsMutation,
    acceptPSATermsMutation,
    queryClient,
    dispatch,
  ]);

  if (!popupConfig) return null;

  return (
    <ConfirmPopup
      Icon={() => <OutlineWarning01Icon size={80} color={colors.warning[500]} />}
      title={popupConfig.title}
      titleStyle={{
        marginTop: 24,
      }}
      description={popupConfig.description}
      descriptionStyle={{
        color: colors.neutral[500],
      }}
      rightButtonText="Confirm"
      rightButtonAction={handleOnPress}
      rightButtonLoading={isLoading}
      rightButtonDisabled={isLoading || !isAccepted}
    >
      <Row
        alignItems="center"
        gap={16}
        marginTop={-32}
        marginBottom={32}
        paddingHorizontal={40}
        justifyContent="center"
      >
        <ReactSwitch
          checked={isAccepted}
          onChange={handleToggleSwitch}
          onColor={colors.secondary[500]}
          onHandleColor="#fff"
          handleDiameter={20}
          uncheckedIcon={false}
          checkedIcon={false}
          offColor={colors.neutral[100]}
          boxShadow="0px 1px 5px rgba(0, 0, 0, 0.35)"
          activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
          height={25}
          width={50}
          className="react-switch"
          id="material-switch"
        />
        <Typography as="span" color={colors.neutral[500]}>{renderSwitchText(popupConfig.type, popupConfig.links)}</Typography>
      </Row>
    </ConfirmPopup>
  );
};
